import { Box, DialogContent, Typography } from "@mui/material";
import moment from "moment";
import { useTranslation } from "react-i18next";
import DialogComponent from "../../Dialog/Dialog";
import {
  IdentifyingDocument,
  hasExpirationDate
} from "../../../types/professional";
import { formatDate } from "../../../utils/date";
import { semanticColors } from "../../../utils/colors";
import { typography } from "../../../utils/typography";

/** Separates one side of the document from the next. */
const sideBlockSx = { mb: 2 };

const rowSx = { display: "flex", alignItems: "center", gap: 1, mb: 2 };

const labelSx = {
  ...typography.body.lBold,
  color: semanticColors.text.primary
};

const valueSx = {
  ...typography.body.l,
  color: semanticColors.text.primary
};

const sideLabelSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary,
  textAlign: "center" as const,
  mt: 0.5
};

/**
 * Capped by height, not width: the scans are wide, and letting them run to the
 * full width pushed the "last updated" line below the fold — which is exactly
 * the thing someone opens this dialog to check.
 */
const imageSx = {
  maxWidth: "100%",
  maxHeight: "260px",
  borderRadius: "8px",
  border: `1px solid ${semanticColors.border.light}`,
  display: "block",
  margin: "0 auto"
};

const missingSx = {
  ...typography.body.m,
  color: semanticColors.text.secondary,
  textAlign: "center" as const,
  py: 3,
  borderRadius: "8px",
  border: `1px dashed ${semanticColors.border.middle}`
};

const footerSx = {
  ...typography.body.s,
  color: semanticColors.text.secondary,
  textAlign: "center" as const,
  mt: 3
};

/**
 * Only an absolute address is an image.
 *
 * A blank or relative `front` would be resolved against this app's own origin,
 * and the browser would happily render whatever asset happens to sit there —
 * which is how a marketing picture from the dashboard ended up standing in for
 * someone's citizen card.
 */
const isImageUrl = (url?: string) => Boolean(url && /^https?:\/\//i.test(url));

function Side({ label, url }: { label: string; url?: string }) {
  const { t } = useTranslation();
  return (
    <Box sx={sideBlockSx}>
      {isImageUrl(url) ? (
        <Box component="img" src={url} alt={label} sx={imageSx} />
      ) : (
        <Typography sx={missingSx}>
          {t("identifyingDocuments.noImage")}
        </Typography>
      )}
      <Typography sx={sideLabelSx}>{label}</Typography>
    </Box>
  );
}

/**
 * The document as it stands: when it expires, both sides of it, and when it was
 * last touched.
 *
 * `updatedAt` is written alongside `createdAt`, so it is present from the start
 * and equals the registration until the document is first edited — which makes
 * it the honest answer to "when was this last updated" either way.
 */
export default function ViewDocumentDialog({
  open,
  onClose,
  title,
  document
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  document: IdentifyingDocument;
}) {
  const { t } = useTranslation();

  return (
    <DialogComponent
      open={open}
      handleModal={onClose}
      headerTxt={title}
      maxWidth="sm"
    >
      <DialogContent>
        <Box sx={rowSx}>
          <Typography sx={labelSx}>
            {t("identifyingDocuments.expiryDate")}
          </Typography>
          <Typography sx={valueSx}>
            {hasExpirationDate(document)
              ? formatDate(document.expirationDate as string)
              : "-"}
          </Typography>
        </Box>

        <Side label={t("identifyingDocuments.front")} url={document.front} />
        <Side label={t("identifyingDocuments.back")} url={document.back} />

        {document.updatedAt && (
          <Typography sx={footerSx}>
            {t("identifyingDocuments.updatedAt", {
              value: moment(document.updatedAt).format("DD/MM/YYYY-HH:mm")
            })}
          </Typography>
        )}
      </DialogContent>
    </DialogComponent>
  );
}
